import React, { useEffect, useState } from "react";
import { resolveHkpTagLookup } from "../../../services/hkpNfcTagApi";
import { PlIcon } from "./plIcons";

/** Saves the HKP tag number / NFC UID; resolves true when the server accepted it. */
export type TagSave = (product: any, srNo: string, xuid: string) => Promise<boolean>;

const text = (value: unknown) => String(value ?? "").trim();

/** Short form of a long NFC UID for chips: 04A1B2…E5F6. */
export const shortUid = (uid: unknown) => {
  const value = text(uid);
  return value.length > 12 ? `${value.slice(0, 6)}…${value.slice(-4)}` : value;
};

/**
 * HKP tag (NFC) in place, where the list's ⋯ sheet and the laptop tag button used to open a modal.
 * Bound: shows the UID and tag number with Change. Not bound: tag number (looked up in the registry,
 * which fills the UID) and UID, Save only when one of them is filled and something changed.
 */
export const NfcTagForm = ({ product, onSave, autoFocus = false }: { product: any; onSave: TagSave; autoFocus?: boolean }) => {
  const boundUid = text(product?.xuid);
  const boundTag = text(product?.srNo);
  const [editing, setEditing] = useState(!boundUid);
  const [tagNo, setTagNo] = useState(boundTag);
  const [uid, setUid] = useState(boundUid);
  const [fromRegistry, setFromRegistry] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // The list reloads after a save; show what the server now holds.
  useEffect(() => {
    setTagNo(boundTag);
    setUid(boundUid);
    setFromRegistry(false);
    setError("");
    setEditing(!boundUid);
  }, [boundTag, boundUid]);

  if (!editing) {
    return (
      <div className="pl-nfc is-bound">
        <PlIcon name="tag" />
        <div className="pl-grow">
          <div className="pl-nfc__title">NFC bound</div>
          <div className="pl-nfc__uid" title="NFC UID (HKP ID)">
            {boundUid}
          </div>
          {boundTag ? <div className="pl-meta">HKP tag no. {boundTag}</div> : null}
        </div>
        <button type="button" className="pl-link" onClick={() => setEditing(true)}>
          Change
        </button>
      </div>
    );
  }

  const changed = text(tagNo) !== boundTag || text(uid) !== boundUid;
  const canSave = Boolean(text(tagNo) || text(uid)) && changed && !error && !saving;

  const lookup = async () => {
    const result = await resolveHkpTagLookup(tagNo, product?.productUniqueId);
    if (result.fromRegistry && result.xuid) {
      setUid(result.xuid);
      setFromRegistry(true);
      setError("");
    } else if (result.error) {
      setError(result.error);
      setFromRegistry(false);
    } else if (fromRegistry) {
      setUid("");
      setFromRegistry(false);
    }
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    const ok = await onSave(product, text(tagNo), text(uid));
    setSaving(false);
    if (ok && boundUid) setEditing(false);
  };

  return (
    <form
      className="pl-nfc"
      onSubmit={(event) => {
        event.preventDefault();
        save();
      }}
    >
      <div className="pl-nfc__fields">
        <label className="pl-field">
          <span>HKP tag no.</span>
          <span className="pl-input">
            <input
              value={tagNo}
              inputMode="numeric"
              placeholder="e.g. 1042"
              aria-label="HKP tag number"
              autoFocus={autoFocus}
              onChange={(event) => {
                setTagNo(event.target.value);
                setError("");
                if (fromRegistry) {
                  setUid("");
                  setFromRegistry(false);
                }
              }}
              onBlur={() => {
                if (text(tagNo) && text(tagNo) !== boundTag) lookup();
              }}
            />
          </span>
        </label>
        <label className="pl-field">
          <span>NFC UID (HKP ID)</span>
          <span className="pl-input">
            <input
              value={uid}
              placeholder="Tap the tag or type"
              aria-label="NFC UID"
              readOnly={fromRegistry}
              onChange={(event) => setUid(event.target.value)}
            />
          </span>
        </label>
      </div>
      {error ? (
        <p className="pl-nfc__error" role="alert">
          {error}
        </p>
      ) : (
        <p className="pl-meta">{fromRegistry ? "UID filled from the tag registry." : "Enter the tag number or the UID."}</p>
      )}
      <div className="pl-nfc__actions">
        {boundUid ? (
          <button type="button" className="pl-link" onClick={() => setEditing(false)}>
            Cancel
          </button>
        ) : null}
        <button type="submit" className="pl-btn is-primary is-sm" disabled={!canSave}>
          {saving ? "Saving…" : "Save tag"}
        </button>
      </div>
    </form>
  );
};
